import React from "react";
import { Star, ExternalLink, Pencil } from "lucide-react";
import { Button } from "./ui/button";
import { google } from "../mock";

function GoogleG({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function GoogleReviewBadge({ variant = "light" }) {
  const dark = variant === "dark";
  const full = Math.floor(google.rating);
  const hasHalf = google.rating - full >= 0.3;

  return (
    <div
      className={`relative rounded-2xl p-6 lg:p-7 border transition-all ${
        dark
          ? "bg-white/5 border-white/10 backdrop-blur text-white hover:bg-white/[0.08]"
          : "bg-white border-slate-200 hover:border-sky-300 hover:shadow-xl hover:shadow-sky-500/10"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 shrink-0 rounded-xl bg-white shadow ring-1 ring-slate-200 flex items-center justify-center">
          <GoogleG className="h-7 w-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-sky-300" : "text-sky-700"}`}>
            Google Reviews
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className={`font-display text-3xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>
              {google.rating.toFixed(1)}
            </span>
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => {
                const isFull = i < full;
                const isHalf = i === full && hasHalf;
                return (
                  <div key={i} className="relative">
                    <Star className={`h-4 w-4 ${dark ? "text-slate-600" : "text-slate-300"}`} />
                    {(isFull || isHalf) && (
                      <div className="absolute inset-0 overflow-hidden" style={{ width: isHalf ? "50%" : "100%" }}>
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <span className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
              ({google.count} reviews)
            </span>
          </div>
          <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
            Based on verified reviews from clients across enterprise, education and industry.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <a href={google.reviewUrl} target="_blank" rel="noopener noreferrer">
          <Button
            className={`h-10 rounded-md ${
              dark
                ? "bg-white text-slate-900 hover:bg-slate-100"
                : "bg-sky-600 hover:bg-sky-700 text-white"
            }`}
          >
            Read on Google
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </a>
        <a href={google.reviewUrl} target="_blank" rel="noopener noreferrer">
          <Button
            variant="outline"
            className={`h-10 rounded-md ${
              dark
                ? "border-white/20 bg-transparent text-white hover:bg-white/10"
                : "border-slate-300 text-slate-800 hover:bg-slate-50"
            }`}
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Write a review
          </Button>
        </a>
      </div>
    </div>
  );
}
